import { useConfigStore } from '@/stores';
import {
    changeFloorAndWaitForEvent,
    getAllFloorsFromIndoorBuildings,
    safeMoveToMarker,
    waitForFloorChangedEvent,
    waitForMapRender,
    type IDataFloorWithBuilding,
} from '@/utils/map';
import { isValidNumber } from '@/utils/number';
import { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';

export type ChangeFloorOptions = {
    moveToCenter?: boolean;
};

export const useFloor = (
    newMap?: DabeeoMap,
    mapData?: DabeeoMapData,
    activeBuildingId?: string,
    mapContainerRef?: RefObject<HTMLElement | null>
) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedFloorName, setSelectedFloorName] = useState<string>('');

    const floorCode = useConfigStore((state) => state?.config?.server?.floor?.floorCode);

    const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
    const currentBuildingId = useConfigStore((state) => state?.config?.server?.building?.id) || '';
    const language = useConfigStore((state) => state?.config?.ui?.language);

    const allFloors = useMemo(() => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        return getAllFloorsFromIndoorBuildings(buildings);
    }, [mapData?.dataBuilding]);

    const defaultActiveBuildingId = useMemo(
        () => buildings?.find((building) => building.id === currentBuildingId)?.studioBuildingId ?? '',
        [buildings, currentBuildingId]
    );

    const resolvedActiveBuildingId = activeBuildingId || defaultActiveBuildingId;

    const floors = useMemo(() => {
        if (!resolvedActiveBuildingId || !buildings) {
            return [];
        }

        return (
            buildings
                .find((building) => building.studioBuildingId === resolvedActiveBuildingId)
                ?.floors.sort((a, b) => a.order - b.order) || []
        );
    }, [buildings, resolvedActiveBuildingId]);

    const getFloorsByBuildingId = useCallback(
        (buildingId: string) => {
            if (!buildings) {
                return [];
            }
            return buildings.find((building) => building.studioBuildingId === buildingId)?.floors.sort((a, b) => a.order - b.order) || [];
        },
        [buildings]
    );

    const getFloorById = useCallback(
        (floorId?: string) => {
            if (!Array.isArray(allFloors) || !allFloors?.length) {
                return undefined;
            }
            return allFloors.find((floor) => floor.id === floorId);
        },
        [allFloors]
    );

    const resolveBuildingIdForCenter = useCallback(
        (options: { floorId?: string; floorName?: string; buildingId?: string }): string => {
            if (options.buildingId) {
                return options.buildingId;
            }
            if (options.floorId) {
                const floor = getFloorById(options.floorId);
                if (floor?.buildingId) {
                    return floor.buildingId;
                }
            }
            if (options.floorName) {
                const floorByLanguage = allFloors.find((floor: IDataFloorWithBuilding) =>
                    floor.name.some((nameItem) => nameItem.lang === language && nameItem.text === options.floorName)
                );
                if (floorByLanguage?.buildingId) {
                    return floorByLanguage.buildingId;
                }
                const floorByName = allFloors.find((floor: IDataFloorWithBuilding) =>
                    floor.name.some((nameItem) => nameItem.text === options.floorName)
                );
                if (floorByName?.buildingId) {
                    return floorByName.buildingId;
                }
            }
            return resolvedActiveBuildingId;
        },
        [allFloors, getFloorById, language, resolvedActiveBuildingId]
    );

    const resolveFloorIdForMove = useCallback(
        (options: { floorId?: string; floorName?: string }): string | undefined => {
            if (options.floorId) {
                return options.floorId;
            }
            if (options.floorName) {
                const floorByLanguage = allFloors.find((floor: IDataFloorWithBuilding) =>
                    floor.name.some((nameItem) => nameItem.lang === language && nameItem.text === options.floorName)
                );
                if (floorByLanguage?.id) {
                    return floorByLanguage.id;
                }
                const floorByName = allFloors.find((floor: IDataFloorWithBuilding) =>
                    floor.name.some((nameItem) => nameItem.text === options.floorName)
                );
                if (floorByName?.id) {
                    return floorByName.id;
                }
            }
            return undefined;
        },
        [allFloors, language]
    );

    const waitForFloorChangeReady = useCallback(async (): Promise<void> => {
        if (mapContainerRef?.current) {
            await waitForFloorChangedEvent(mapContainerRef.current);
            return;
        }
        await waitForMapRender(5);
        await new Promise<void>((resolve) => {
            setTimeout(resolve, 150);
        });
    }, [mapContainerRef]);

    const moveToBuildingCenter = useCallback(
        async (buildingId: string, floorId?: string) => {
            if (!newMap || !buildingId) {
                return;
            }
            await waitForMapRender(3);
            const center = newMap.context?.getBuildingCenterPosition?.(buildingId);
            if (!center || !isValidNumber(center.x) || !isValidNumber(center.y)) {
                return;
            }
            await safeMoveToMarker(
                newMap,
                { x: center.x, y: center.y },
                floorId,
                true,
                3,
                'moveToBuildingCenter'
            );
        },
        [newMap]
    );

    const handleChangeFloorByName = useCallback(
        async (floorName?: string, buildingId?: string, options?: ChangeFloorOptions) => {
            try {
                if (!newMap || !floorName) return;
                setLoading(true);
                if (mapContainerRef?.current) {
                    const waitPromise = waitForFloorChangedEvent(mapContainerRef.current);
                    await newMap.context?.changeFloorByName(floorName || '1F');
                    setSelectedFloorName(floorName);
                    await waitPromise;
                } else {
                    await newMap.context?.changeFloorByName(floorName || '1F');
                    setSelectedFloorName(floorName);
                    await waitForFloorChangeReady();
                }
                if (options?.moveToCenter) {
                    const targetBuildingId = resolveBuildingIdForCenter({ floorName, buildingId });
                    const targetFloorId = resolveFloorIdForMove({ floorName });
                    await moveToBuildingCenter(targetBuildingId, targetFloorId);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [mapContainerRef, moveToBuildingCenter, newMap, resolveBuildingIdForCenter, resolveFloorIdForMove, waitForFloorChangeReady]
    );

    const handleChangeFloor = useCallback(
        async (floorId?: string, buildingId?: string, options?: ChangeFloorOptions) => {
            try {
                if (!newMap || !floorId) return;
                setLoading(true);
                if (mapContainerRef?.current) {
                    await changeFloorAndWaitForEvent(newMap, mapContainerRef.current, floorId);
                } else {
                    await newMap.context?.changeFloor(floorId ?? '');
                    await waitForFloorChangeReady();
                }
                if (options?.moveToCenter) {
                    const targetBuildingId = resolveBuildingIdForCenter({ floorId, buildingId });
                    await moveToBuildingCenter(targetBuildingId, floorId);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [mapContainerRef, moveToBuildingCenter, newMap, resolveBuildingIdForCenter, waitForFloorChangeReady]
    );

    const selectedFloor = useMemo(() => {
        if (!Array.isArray(floors) || !floors?.length) {
            return null;
        }
        return floors.find((floor) => floor?.name[language] === selectedFloorName);
    }, [floors, selectedFloorName, language]);

    useEffect(() => {
        if (!Array.isArray(floors) || !floors.length) {
            return;
        }

        setSelectedFloorName((prev) => {
            if (prev && floors.some((floor) => floor?.name?.[language] === prev)) {
                return prev;
            }

            if (floorCode) {
                const matchedByCode = floors.find(
                    (floor) =>
                        (floor as { floor_code?: string; floorCode?: string }).floor_code === floorCode ||
                        floor.floorCode === floorCode
                );
                if (matchedByCode?.name?.[language]) {
                    return matchedByCode.name[language];
                }
            }

            const defaultFloor = floors.find((floor) => floor.order === 0) ?? floors[0];
            return defaultFloor?.name?.[language] || '';
        });
    }, [floors, floorCode, language]);

    return {
        floors,
        handleChangeFloor,
        loading,
        selectedFloor,
        getFloorById,
        handleChangeFloorByName,
        selectedFloorName,
        setSelectedFloorName,
        getFloorsByBuildingId,
    };
};
